import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, Tab, Tabs, Typography, type SxProps, type Theme } from '@mui/material'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import { baseModalDefaultFooterSx } from '@/components/common/base'
import { PreviewableImage } from '@/components/common/imagePreview'
import {
  ExpandUnderlineField,
  expandInfoPanelSx,
} from '@/components/admin/_shared/giaoDich/GiaoDichExpandKiotFields'
import {
  kiotExpandDetailEmptyHintSx,
  kiotExpandDetailFooterSx,
  kiotExpandDetailGeneralPanelSx,
  kiotExpandDetailHeaderActionsSx,
  kiotExpandDetailHeaderBarSx,
  kiotExpandDetailMetaRowSx,
  kiotExpandDetailStatCardSx,
  kiotExpandDetailTabPanelSx,
  kiotExpandDetailTabsSx,
} from './kiotListExpandTokens'
import {
  KiotListExpandModalContext,
  useKiotListExpandInModal,
  useKiotListExpandModal,
} from './kiotListExpandModalContext'

export type KiotListExpandTabItem = {
  value: string
  label: React.ReactNode
  panel: React.ReactNode
  disabled?: boolean
}

export type KiotListExpandDetailTabsProps = {
  tabs: KiotListExpandTabItem[]
  defaultTab?: string
  /** Controlled — khi có thì ưu tiên hơn state nội bộ (đồng bộ `?tab=`). */
  tab?: string
  /**
   * Nút hành động — hiện cùng hàng title tab (căn phải) và dưới panel.
   * Truyền hàm để mỗi slot mount node riêng (tránh reuse 1 React element).
   */
  actions?: React.ReactNode | (() => React.ReactNode)
  /** Nút riêng cho hàng title tab (mặc định = `actions`) — vd. ẩn nút đã có trong menu ⋯ của dòng. */
  headerActions?: React.ReactNode | (() => React.ReactNode)
  /** Footer tùy chỉnh — nếu không có `actions` thì dùng như trước. */
  footer?: React.ReactNode
  /** Hiện thanh tab kể cả khi chỉ có 1 tab (vd. YCSC chỉ «Thông tin»). */
  alwaysShowTabs?: boolean
  /** Báo parent khi đổi tab (vd. đổi CTA theo tab Phiếu thu / Phiếu chi). */
  onTabChange?: (value: string) => void
  /** Override style vùng panel (vd. trang detail: flex + ẩn scroll ngoài để paginate sát đáy). */
  panelSx?: SxProps<Theme>
  /** Nội dung bên trái footer (vd. phân trang thẻ kho) — cùng hàng với `actions`. */
  footerLeft?: React.ReactNode
}

const resolveActions = (actions?: React.ReactNode | (() => React.ReactNode)) =>
  typeof actions === 'function' ? actions() : actions

export const KiotListExpandDetailTabs: React.FC<KiotListExpandDetailTabsProps> = ({
  tabs,
  defaultTab,
  tab: tabControlled,
  actions,
  headerActions,
  footer,
  alwaysShowTabs = false,
  onTabChange,
  panelSx,
  footerLeft,
}) => {
  const firstTab = tabs[0]?.value ?? ''
  const [activeTab, setActiveTab] = useState(defaultTab ?? firstTab)
  const resolvedTab = tabControlled ?? activeTab
  const safeTab = tabs.some((t) => t.value === resolvedTab) ? resolvedTab : firstTab
  const activePanel = tabs.find((t) => t.value === safeTab)?.panel
  const showTabBar = tabs.length > 1 || (alwaysShowTabs && tabs.length > 0)
  const hasActions = actions != null
  const hasFooterBar = hasActions || footerLeft != null
  const modalCtx = useKiotListExpandModal()
  const inModal = modalCtx != null
  const showHeaderBar = showTabBar || (hasActions && !inModal)
  const tabPanelSx = panelSx
    ? ([kiotExpandDetailTabPanelSx, panelSx] as SxProps<Theme>)
    : kiotExpandDetailTabPanelSx

  const handleTabChange = (_: React.SyntheticEvent, value: string) => {
    if (tabControlled == null) setActiveTab(value)
    onTabChange?.(value)
  }

  if (inModal) {
    const footerNode = hasActions ? resolveActions(actions) : footer
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
        }}
      >
        {showTabBar ? (
          <Box sx={kiotExpandDetailHeaderBarSx}>
            <Tabs
              value={safeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={kiotExpandDetailTabsSx}
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} disabled={tab.disabled} disableRipple />
              ))}
            </Tabs>
          </Box>
        ) : null}
        <Box sx={tabPanelSx} data-kiot-expand-body-scroll>
          {/* Chỉ tầng tabs ngoài cùng đẩy nút xuống footer modal; tabs lồng trong panel giữ nguyên. */}
          <KiotListExpandModalContext.Provider value={null}>{activePanel}</KiotListExpandModalContext.Provider>
        </Box>
        {footerNode != null && modalCtx.footerHost ? createPortal(footerNode, modalCtx.footerHost) : null}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        flex: '1 1 auto',
        overflow: 'hidden',
      }}
    >
      {showHeaderBar ? (
        <Box sx={kiotExpandDetailHeaderBarSx}>
          {showTabBar ? (
            <Tabs
              value={safeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={kiotExpandDetailTabsSx}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  disabled={tab.disabled}
                  disableRipple
                />
              ))}
            </Tabs>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {hasActions ? (
            <Box sx={kiotExpandDetailHeaderActionsSx}>{resolveActions(headerActions ?? actions)}</Box>
          ) : null}
        </Box>
      ) : null}
      <Box sx={tabPanelSx} data-kiot-expand-body-scroll>
        {activePanel}
      </Box>
      {hasFooterBar ? (
        <KiotListExpandDetailFooter
          sx={{
            justifyContent: footerLeft != null ? 'space-between' : 'flex-end',
            gap: 0.75,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
          left={footerLeft}
          right={hasActions ? resolveActions(actions) : null}
        />
      ) : (
        footer
      )}
    </Box>
  )
}

export type KiotListExpandInfoFieldLayout = 'stacked' | 'inline'

export type KiotListExpandInfoFieldItem = {
  label: string
  value: React.ReactNode
  span?: 1 | 2 | 3 | 4
  /** Cho phép xuống dòng (địa chỉ, ghi chú). Mặc định ellipsis 1 dòng. */
  multiline?: boolean
  /** Ẩn field khi false (tiện declarative). */
  hidden?: boolean
  /** @deprecated Field luôn theo style expand chung (label trái, value phải). */
  layout?: KiotListExpandInfoFieldLayout
}

export type KiotListExpandInfoFieldProps = KiotListExpandInfoFieldItem

/** Field label + value — cùng style field expand dùng chung (`ExpandUnderlineField`). */
export const KiotListExpandInfoField: React.FC<KiotListExpandInfoFieldProps> = ({
  label,
  value,
  span = 1,
  multiline = false,
}) => (
  <Box
    sx={{
      minWidth: 0,
      gridColumn: span > 1 ? { xs: '1 / -1', lg: span >= 3 ? '1 / -1' : `span ${span}` } : undefined,
    }}
  >
    <ExpandUnderlineField
      label={label}
      value={value}
      multiline={multiline}
      multilineRows={multiline ? 1 : undefined}
    />
  </Box>
)

export type KiotListExpandInfoGridProps = {
  children: React.ReactNode
}

/** Khối field — dùng `expandInfoPanelSx` chung của panel mở rộng dòng. */
export const KiotListExpandInfoGrid: React.FC<KiotListExpandInfoGridProps> = ({ children }) => (
  <Box sx={expandInfoPanelSx}>{children}</Box>
)

export type KiotListExpandInfoAvatarProps = {
  src?: string | null
  size?: number
  alt?: string
  /** Bộ ảnh xem khi bấm phóng to (mặc định chỉ `src`). */
  previewImages?: Array<string | null | undefined>
}

/** Avatar / thumbnail chuẩn cho tab Thông tin — bấm để xem ảnh lớn. */
export const KiotListExpandInfoAvatar: React.FC<KiotListExpandInfoAvatarProps> = ({
  src,
  size = 88,
  alt = '',
  previewImages,
}) => {
  const url = src != null && String(src).trim() !== '' ? String(src).trim() : ''
  if (url) {
    return (
      <PreviewableImage
        src={url}
        images={previewImages}
        alt={alt}
        sx={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: '4px',
          border: '1px solid',
          borderColor: 'divider',
          display: 'block',
        }}
      />
    )
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        bgcolor: '#f5f5f5',
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  )
}

export type KiotListExpandInfoTabDensity = 'default' | 'compact'

export type KiotListExpandInfoTabProps = {
  /** Ảnh / avatar bên trái (optional). */
  media?: React.ReactNode
  /** Tiêu đề chính — thường là tên bản ghi. */
  title?: React.ReactNode
  /** Dòng phụ dưới title (mã, nhóm…). */
  subtitle?: React.ReactNode
  /** Chip / badge trạng thái dưới subtitle. */
  meta?: React.ReactNode
  /** `compact`: header 1 dòng + field inline — tiết kiệm chiều cao panel expand. */
  density?: KiotListExpandInfoTabDensity
  /** @deprecated Field luôn theo style expand chung (label trái, value phải). */
  fieldLayout?: KiotListExpandInfoFieldLayout
  /** Fields declarative — dùng chung nhiều màn. */
  fields?: KiotListExpandInfoFieldItem[]
  /** Nội dung bổ sung dưới grid (hoặc thay thế fields). */
  children?: React.ReactNode
  /** Link / action phụ dưới fields. */
  footerLinks?: React.ReactNode
}

/**
 * Base tab "Thông tin" cho panel mở rộng danh sách.
 * Layout: [media] + title/subtitle/meta + grid fields — dùng lại KH, NCC, SP…
 */
export const KiotListExpandInfoTab: React.FC<KiotListExpandInfoTabProps> = ({
  media,
  title,
  subtitle,
  meta,
  density = 'default',
  fields,
  children,
  footerLinks,
}) => {
  const isCompact = density === 'compact'
  const visibleFields = (fields ?? []).filter((f) => !f.hidden)
  const hasHeader = title != null || subtitle != null || meta != null

  return (
    <Box
      sx={{
        display: 'flex',
        gap: isCompact ? 1.25 : 2,
        px: isCompact ? 2 : 2.5,
        py: isCompact ? 1.25 : 2,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        bgcolor: '#fff',
        alignItems: 'flex-start',
      }}
    >
      {media ? <Box sx={{ flexShrink: 0 }}>{media}</Box> : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {hasHeader ? (
          <Box sx={{ mb: visibleFields.length || children || footerLinks ? (isCompact ? 0.75 : 1.5) : 0 }}>
            {isCompact ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                {title != null ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: banhangKiotLike.kiotTextPrimary,
                      lineHeight: banhangKiotLike.kiotLineHeight,
                      fontFamily: banhangKiotLike.kiotFontStack,
                      flexShrink: 0,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle != null ? (
                  <Typography
                    component="span"
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
                      fontWeight: 500,
                      color: banhangKiotLike.kiotTextPrimary,
                      lineHeight: banhangKiotLike.kiotLineHeight,
                      fontFamily: banhangKiotLike.kiotFontStack,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
                {meta != null ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 'auto',
                      flexShrink: 0,
                    }}
                  >
                    {meta}
                  </Box>
                ) : null}
              </Box>
            ) : (
              <>
                {title != null ? (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: banhangKiotLike.kiotTextPrimary,
                      lineHeight: 1.35,
                      mb: subtitle != null || meta != null ? 0.35 : 0,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle != null ? (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.8125rem',
                      color: banhangKiotLike.kiotTextMuted,
                      lineHeight: 1.4,
                      mb: meta != null ? 1 : 0,
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
                {meta != null ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{meta}</Box>
                ) : null}
              </>
            )}
          </Box>
        ) : null}
        {visibleFields.length > 0 ? (
          <KiotListExpandInfoGrid>
            {visibleFields.map((field, idx) => (
              <KiotListExpandInfoField
                key={`${field.label}-${idx}`}
                label={field.label}
                value={field.value}
                span={field.span}
                multiline={field.multiline}
              />
            ))}
          </KiotListExpandInfoGrid>
        ) : null}
        {children}
        {footerLinks != null ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.25 }}>{footerLinks}</Box>
        ) : null}
      </Box>
    </Box>
  )
}

export type KiotListExpandGeneralPanelProps = {
  meta?: React.ReactNode
  children: React.ReactNode
}

/** @deprecated Ưu tiên `KiotListExpandInfoTab` — giữ để tương thích tab mô tả / panel cũ. */
export const KiotListExpandGeneralPanel: React.FC<KiotListExpandGeneralPanelProps> = ({ meta, children }) => (
  <Box sx={kiotExpandDetailGeneralPanelSx}>
    {meta ? <Box sx={kiotExpandDetailMetaRowSx}>{meta}</Box> : null}
    {children}
  </Box>
)

export type KiotListExpandDebtStat = {
  label: string
  value: React.ReactNode
  highlight?: boolean
}

export type KiotListExpandDebtTabPanelProps = {
  stats: KiotListExpandDebtStat[]
  hint: React.ReactNode
}

export const KiotListExpandDebtTabPanel: React.FC<KiotListExpandDebtTabPanelProps> = ({ stats, hint }) => (
  <Box sx={{ px: 2.5, py: 2 }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(168px, 220px))' },
        gap: 1.5,
        mb: 2,
      }}
    >
      {stats.map((stat) => (
        <Box
          key={stat.label}
          sx={
            [
              kiotExpandDetailStatCardSx,
              stat.highlight
                ? {
                    borderColor: 'rgba(0, 112, 244, 0.22)',
                    bgcolor: '#f5f9ff',
                  }
                : {},
            ] as SxProps<Theme>
          }
        >
          <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5, lineHeight: 1.35, fontWeight: 500 }}>
            {stat.label}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: stat.highlight ? banhangKiotLike.linkBlue : banhangKiotLike.kiotTextPrimary,
              lineHeight: 1.2,
            }}
          >
            {stat.value}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box sx={kiotExpandDetailEmptyHintSx}>
      <ReceiptLongOutlinedIcon sx={{ fontSize: 28, color: 'rgba(0,0,0,0.22)', mb: 0.75 }} />
      <Typography sx={{ fontSize: '0.8125rem', color: '#666', lineHeight: 1.5, maxWidth: 360 }}>{hint}</Typography>
    </Box>
  </Box>
)

export type KiotListExpandDetailFooterProps = {
  left?: React.ReactNode
  right?: React.ReactNode
  sx?: SxProps<Theme>
}

export const KiotListExpandDetailFooter: React.FC<KiotListExpandDetailFooterProps> = ({ left, right, sx }) => {
  const inModal = useKiotListExpandInModal()
  return (
    <Box
      sx={
        (inModal
          ? { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }
          : [
              baseModalDefaultFooterSx,
              kiotExpandDetailFooterSx,
              { justifyContent: 'space-between' },
              ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
            ]) as SxProps<Theme>
      }
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{left}</Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{right}</Box>
    </Box>
  )
}
